import { InfoCircleOutlined } from "@ant-design/icons";
import useGetDeliverableConfigurators from "@entities/deliverableConfigurator/lib/hooks/useGetDeliverableConfigurators";
import useGetDeliverableMediaRights from "@entities/deliverableConfigurator/lib/hooks/useGetDeliverableMediaRights";
import { IPurchaseItems } from "@shared/lib/types";
import {
  Button,
  Card,
  Divider,
  Popover,
  Typography,
} from "@shared/ui";
import { TextWithTooltip } from "@shared/ui/TextWithTooltip";
import { Flex, Input, InputNumber, Select, Tag } from "antd";
import { Megaphone, Plus, Trash2 } from "lucide-react";
import React, { useEffect, useMemo, useRef, useState } from "react";
import styles from "./DeliverableFormats.module.scss";

const { Option } = Select;
const { TextArea } = Input;

export interface ILocalDeliverable extends Omit<IPurchaseItems, "_id"> {
  _id?: string;
  clientSideId: string;
  isExisting?: boolean;
  isNewlyAdded?: boolean;
  isDeleted?: boolean;
  isCancelled?: boolean;
  adRightsLinkedToContentPurchaseItemId?: string;
  linkedContentIds?: string[];
  category?: string;
}

export interface DeliverablesConfiguratorListProps {
  deliverables: ILocalDeliverable[];
  onChange?: (deliverables: ILocalDeliverable[]) => void;
  onAdd?: () => void;
  onRemove: (index: number) => void;
  onSelectionChange: (index: number, value: string) => void;
  onValueChange: (index: number, field: string, value: any) => void;
  onSubTypeValueChange: (
    index: number,
    subTypeName: string,
    value: string | undefined
  ) => void;
  disabled?: boolean;
  disableEditingExisting?: boolean;
  restrictAdd?: boolean;
  restrictDelete?: boolean;
  minItemsToRestrictDelete?: number;
}

const PRESET_DURATIONS = ["15 days", "30 days", "60 days", "90 days", "6 months", "1 year"];

export const DeliverableFormats: React.FC<DeliverablesConfiguratorListProps> = ({
  deliverables,
  onChange,
  onAdd,
  onRemove,
  onSelectionChange,
  onValueChange,
  onSubTypeValueChange,
  disabled = false,
  disableEditingExisting = false,
  restrictAdd = false,
  restrictDelete = false,
  minItemsToRestrictDelete = 0,
}) => {
  const [topSelectValue, setTopSelectValue] = useState<string | null>(null);
  const [customDays, setCustomDays] = useState<number | null>(null);
  const [activePopoverIndex, setActivePopoverIndex] = useState<number | null>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const prevLengthRef = useRef(deliverables.length);

  useEffect(() => {
    if (deliverables.length > prevLengthRef.current) {
      const newlyAdded = deliverables[deliverables.length - 1];
      if (newlyAdded) {
        const idKey = newlyAdded.clientSideId || (deliverables.length - 1);
        setTimeout(() => {
          const element = document.getElementById(`deliverable-card-${idKey}`);
          if (element) {
            element.scrollIntoView({
              behavior: "smooth",
              block: "nearest",
            });
          }
        }, 100);
      }
    }
    prevLengthRef.current = deliverables.length;
  }, [deliverables.length]);

  const { deliverableConfiguratorsData, isDeliverableConfiguratorsFetching } =
    useGetDeliverableConfigurators();
  const { deliverableMediaRightsData, isDeliverableMediaRightsFetching } =
    useGetDeliverableMediaRights();

  const configurators = useMemo(() => {
    return deliverableConfiguratorsData?.data?.data || [];
  }, [deliverableConfiguratorsData]);

  // Options for top deliverable format dropdown (EXCLUDING Ad-Rights)
  const contentFormatOptions = useMemo(() => {
    const selectedValues = deliverables
      .filter(
        (d) =>
          d.contentDeliverableConfiguratorId &&
          d.contentDeliverableConfiguratorDeliverableId &&
          !d.isCancelled
      )
      .map(
        (d) =>
          `${d.contentDeliverableConfiguratorId}|${d.contentDeliverableConfiguratorDeliverableId}`
      );
    if (!configurators) return [];
    return configurators.flatMap((configurator) =>
      configurator.deliverables
        .filter((deliverable) => {
          const value = `${configurator._id}|${deliverable._id}`;
          return !deliverable.isArchived || selectedValues.includes(value);
        })
        .map((deliverable) => ({
          label: `${configurator.platform} - ${deliverable.category}`,
          value: `${configurator._id}|${deliverable._id}`,
          disabled: deliverable.isArchived,
        }))
    );
  }, [configurators, deliverables]);

  const getSelectedConfiguratorFormat = (
    configuratorId: string,
    deliverableId: string
  ) => {
    if (!configuratorId || !deliverableId) return null;
    return configurators
      .find((cfg) => cfg._id === configuratorId)
      ?.deliverables.find((fmt) => fmt._id === deliverableId);
  };

  const getFormatLabel = (d: ILocalDeliverable) => {
    const selectedFormat = getSelectedConfiguratorFormat(
      d.contentDeliverableConfiguratorId || "",
      d.contentDeliverableConfiguratorDeliverableId || ""
    );
    if (selectedFormat) {
      const platform =
        configurators.find((cfg) => cfg._id === d.contentDeliverableConfiguratorId)
          ?.platform || "";
      return `${platform} ${selectedFormat.category}`;
    }
    return d.contentCategory || "Format";
  };

  const handleTopFormatSelect = (value: string) => {
    if (!value) return;
    const emptyIndex = deliverables.findIndex(
      (d) =>
        d.purchaseItemType === "Content" &&
        !d.contentDeliverableConfiguratorId
    );

    if (emptyIndex !== -1) {
      onSelectionChange(emptyIndex, value);
    } else {
      const [configuratorId, deliverableId] = value.split("|");
      const delConfig = configurators.find((cfg) => cfg._id === configuratorId);
      const format = delConfig?.deliverables.find(
        (fmt) => fmt._id === deliverableId
      );

      const newItem: ILocalDeliverable = {
        clientSideId: Math.random().toString(36).substring(2, 9),
        purchaseItemType: "Content",
        contentCategory: format?.category || "",
        contentSubType: {},
        contentType: "",
        adRightsDurations: "",
        adRightsNotes: "",
        adRightsType: "",
        adRightsDescription: "",
        contentDeliverableConfiguratorId: configuratorId,
        contentDeliverableConfiguratorDeliverableId: deliverableId,
        adRightsLinkedToContentPurchaseItemId: undefined,
        contentDeliverableConfigurator: delConfig,
        isExisting: false,
      };

      if (onChange) {
        onChange([...deliverables, newItem]);
      }
    }
    setTopSelectValue(null);
  };

  const handleAddAdRights = () => {
    const newAdRights: ILocalDeliverable = {
      clientSideId: Math.random().toString(36).substring(2, 9),
      purchaseItemType: "Ad-Rights",
      contentCategory: "Ad Rights",
      contentSubType: {},
      contentType: "",
      adRightsDurations: "",
      adRightsNotes: "",
      adRightsType: "",
      adRightsDescription: "",
      contentDeliverableConfiguratorId: "",
      contentDeliverableConfiguratorDeliverableId: "",
      adRightsLinkedToContentPurchaseItemId: undefined,
      isExisting: false,
    };
    if (onChange) {
      onChange([...deliverables, newAdRights]);
    }
  };

  const contentDeliverables = useMemo(() => {
    return deliverables.filter(
      (d) =>
        d.purchaseItemType === "Content" &&
        d.contentDeliverableConfiguratorId &&
        d.contentDeliverableConfiguratorDeliverableId &&
        !d.isDeleted &&
        !d.isCancelled
    );
  }, [deliverables]);

  const adRightsDeliverables = useMemo(() => {
    return deliverables
      .map((d, index) => ({ item: d, actualIndex: index }))
      .filter(({ item }) => item.purchaseItemType === "Ad-Rights" && !item.isCancelled);
  }, [deliverables]);

  const handleToggleAppliesTo = (
    adRightsIndex: number,
    adRightsItem: ILocalDeliverable,
    contentDel: ILocalDeliverable
  ) => {
    const isItemDisabled =
      disabled ||
      (disableEditingExisting && adRightsItem.isExisting) ||
      adRightsItem.isDeleted;
    if (isItemDisabled) return;

    const currentLinkedId =
      adRightsItem.adRightsLinkedToContentPurchaseItemId ||
      adRightsItem.linkedContentIds?.[0];

    const targetId = contentDel.clientSideId || contentDel._id;

    const isCurrentlyLinked =
      Boolean(currentLinkedId) &&
      (currentLinkedId === contentDel.clientSideId ||
        (Boolean(contentDel._id) && currentLinkedId === contentDel._id));

    const newLinkedId = isCurrentlyLinked ? undefined : targetId;

    onValueChange(
      adRightsIndex,
      "adRightsLinkedToContentPurchaseItemId",
      newLinkedId
    );
    if (adRightsItem.linkedContentIds !== undefined) {
      onValueChange(
        adRightsIndex,
        "linkedContentIds",
        newLinkedId ? [newLinkedId] : []
      );
    }
  };

  return (
    <Flex ref={scrollContainerRef} vertical gap={5} style={{ width: "100%", maxHeight: 300, overflowY: "auto" }}>
      {/* 1. Single dropdown for selecting deliverables on top (excluding ad rights) */}
      {!restrictAdd && !disabled && (
        <Flex vertical gap={4}>
          <Select
            size="small"
            className={styles.topSelect}
            popupClassName={styles.topSelectDropdown}
            style={{ width: "100%", height: 25 }}
            placeholder="Search Deliverable..."
            loading={isDeliverableConfiguratorsFetching}
            disabled={disabled}
            value={topSelectValue}
            onChange={handleTopFormatSelect}
            options={contentFormatOptions}
            showSearch
            filterOption={(input, option) =>
              (option?.label?.toString().toLowerCase() ?? "").includes(
                input.toLowerCase()
              )
            }
            allowClear
          />
        </Flex>
      )}

      {/* 2. Format Cards Container */}
      <div>
        {deliverables.map((deliverable, actualIndex) => {
          if (deliverable.isCancelled) return null;
          if (deliverable.purchaseItemType === "Ad-Rights") return null;
          if (!deliverable.contentDeliverableConfiguratorId) return null;

          const selectedFormat = getSelectedConfiguratorFormat(
            deliverable.contentDeliverableConfiguratorId || "",
            deliverable.contentDeliverableConfiguratorDeliverableId || ""
          );
          const subTypes =
            selectedFormat?.subTypes?.filter((st) => !st.isArchived) || [];
          const isItemDisabled =
            disabled ||
            (disableEditingExisting && deliverable.isExisting) ||
            deliverable.isDeleted;
          const showDelete =
            !restrictDelete &&
            !disabled &&
            !deliverable.isDeleted;

          const cardClassName = `${styles.deliverableCard} ${
            deliverable.isDeleted
              ? styles.deletedCard
              : deliverable.isNewlyAdded
              ? styles.newlyAddedCard
              : ""
          }`;

          return (
            <Card
              key={deliverable.clientSideId || actualIndex}
              id={`deliverable-card-${deliverable.clientSideId || actualIndex}`}
              className={cardClassName}
              title={getFormatLabel(deliverable)}
              styles={{
                  header: {
                    marginBottom: 0
                  }
                }}
              extra={
                showDelete ? (
                  <Button
                    size="small"
                    type="text"
                    icon={<Trash2 size={13} />}
                    onClick={() => onRemove(actualIndex)}
                    className={styles.deleteButton}
                  />
                ) : null
              }
            >
              {subTypes.length > 0 ? (
                <Flex vertical gap={6}>
                  {subTypes.map((subType) => (
                    <Flex
                      key={subType.name}
                      vertical
                      gap={2}
                      style={{ width: "100%" }}
                    >
                      <span className={styles.label}>{subType.name}</span>
                      <Flex wrap gap={4} style={{ width: "100%" }}>
                        {subType.values
                          .filter(
                            (v) =>
                              !isItemDisabled ||
                              deliverable.contentSubType?.[subType.name] === v
                          )
                          .map((v) => {
                          const isChecked =
                            deliverable.contentSubType?.[subType.name] === v;
                          return (
                            <Tag.CheckableTag
                              key={v}
                              checked={isChecked}
                              className={
                                isChecked
                                  ? styles.appliesToTagChecked
                                  : styles.appliesToTag
                              }
                              style={
                                isItemDisabled
                                  ? { pointerEvents: "none", opacity: 0.6 }
                                  : undefined
                              }
                              onChange={(checked) => {
                                if (!isItemDisabled) {
                                  onSubTypeValueChange(
                                    actualIndex,
                                    subType.name,
                                    checked ? v : undefined
                                  );
                                }
                              }}
                              onClick={() => {
                                if (!isItemDisabled) {
                                  onSubTypeValueChange(
                                    actualIndex,
                                    subType.name,
                                    isChecked ? undefined : v
                                  );
                                }
                              }}
                            >
                              {v}
                            </Tag.CheckableTag>
                          );
                        })}
                      </Flex>
                    </Flex>
                  ))}
                </Flex>
              ) : (
                <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                  Standard format with no additional sub-types.
                </Typography.Text>
              )}
            </Card>
          );
        })}
      </div>

      {/* 3. Horizontal Divider & Ad Rights Section */}
      <Divider style={{ margin: 0, color: "#f1f5f9" }} />
      <Flex justify="space-between" align="center">
        <Flex gap={4} align="center">
          <Megaphone size={10} color="#9ba3b2" />
          <span className={styles.adRightsHeaderLabel}>Ad rights</span>
        </Flex>
        {!disabled && (
          <Button
            type="link"
            size="small"
            icon={<Plus size={10} />}
            onClick={handleAddAdRights}
            style={{
              fontSize: 10,
              fontWeight: 600,
              color: "#635bff",
              padding: 0,
              height: "auto",
            }}
          >
            Add
          </Button>
        )}
      </Flex>

      <div>
        {adRightsDeliverables?.length > 0 ? adRightsDeliverables.map(
          ({ item: adRightsItem, actualIndex: adRightsIndex }) => {
            const isItemDisabled =
              disabled ||
              (disableEditingExisting && adRightsItem.isExisting) ||
              adRightsItem.isDeleted;

            const linkedId =
              adRightsItem.adRightsLinkedToContentPurchaseItemId ||
              adRightsItem.linkedContentIds?.[0];

            const hasLinkedFormat =
              Boolean(linkedId) &&
              contentDeliverables.some(
                (contentDel) =>
                  linkedId === contentDel.clientSideId ||
                  (Boolean(contentDel._id) && linkedId === contentDel._id)
              );

            const showAppliesToRow = !isItemDisabled || hasLinkedFormat;

            const adRightsCardClassName = `${styles.deliverableCard} ${
              adRightsItem.isDeleted
                ? styles.deletedCard
                : adRightsItem.isNewlyAdded
                ? styles.newlyAddedCard
                : ""
            }`;

            const isPopoverOpen = activePopoverIndex === adRightsIndex;

            return (
              <Card
                key={adRightsItem.clientSideId || adRightsIndex}
                id={`deliverable-card-${adRightsItem.clientSideId || adRightsIndex}`}
                className={adRightsCardClassName}
                title={
                  <span className={styles.adRightsHeaderLabel}>
                    Ad Rights
                  </span>
                }
                styles={{
                  header: {
                    marginBottom: 0
                  }
                }}
                extra={
                  !disabled && !adRightsItem.isDeleted ? (
                    <Button
                      size="small"
                      type="text"
                      icon={<Trash2 size={13} />}
                      onClick={() => onRemove(adRightsIndex)}
                      className={styles.deleteButton}
                    />
                  ) : null
                }
              >
                <Flex vertical gap={8} style={{ width: "100%" }}>
                  {/* Content Rights Type Checkable Tags */}
                  <Flex vertical gap={2}>
                    <span className={styles.adRightsSubLabel}>
                      Type
                    </span>
                    <Flex wrap gap={4} align="center">
                      {deliverableMediaRightsData?.data?.types
                        ?.filter(
                          (type: any) =>
                            !isItemDisabled ||
                            adRightsItem.adRightsType === type.name
                        )
                        .map((type: any) => {
                          const isChecked =
                            adRightsItem.adRightsType === type.name;
                          return (
                            <Tag.CheckableTag
                              key={type.name}
                              checked={isChecked}
                              className={
                                isChecked
                                  ? styles.appliesToTagChecked
                                  : styles.appliesToTag
                              }
                              style={
                                isItemDisabled
                                  ? { pointerEvents: "none", opacity: 0.6 }
                                  : undefined
                              }
                              onChange={(checked) => {
                                if (!isItemDisabled) {
                                  onValueChange(
                                    adRightsIndex,
                                    "adRightsType",
                                    checked ? type.name : ""
                                  );
                                }
                              }}
                            >
                              <Flex gap={4} align="center">
                                {type.description && (
                                  <Popover
                                    content={
                                      <Typography.Text
                                        type="secondary"
                                        className={styles.adRightsNotes}
                                      >
                                        {type.description}
                                      </Typography.Text>
                                    }
                                    trigger="hover"
                                    placement="bottom"
                                  >
                                    <InfoCircleOutlined
                                      style={{ fontSize: 10, marginTop: 1 }}
                                    />
                                  </Popover>
                                )}
                                {type.name}
                              </Flex>
                            </Tag.CheckableTag>
                          );
                        })}

                      {(!isItemDisabled ||
                        adRightsItem.adRightsType === "Custom") && (
                        <Tag.CheckableTag
                          key="Custom"
                          checked={adRightsItem.adRightsType === "Custom"}
                          className={
                            adRightsItem.adRightsType === "Custom"
                              ? styles.appliesToTagChecked
                              : styles.appliesToTag
                          }
                          style={
                            isItemDisabled
                              ? { pointerEvents: "none", opacity: 0.6 }
                              : undefined
                          }
                          onChange={(checked) => {
                            if (!isItemDisabled) {
                              onValueChange(
                                adRightsIndex,
                                "adRightsType",
                                checked ? "Custom" : ""
                              );
                            }
                          }}
                        >
                          Custom
                        </Tag.CheckableTag>
                      )}
                    </Flex>
                  </Flex>

                  {/* Type Description & Custom Details */}
                  {Boolean(adRightsItem.adRightsType) && (
                    <Flex vertical gap={5}>
                      {adRightsItem.adRightsType !== "Custom" &&
                        Boolean(
                          deliverableMediaRightsData?.data?.types.find(
                            (t: any) => t.name === adRightsItem.adRightsType
                          )?.description
                        ) && (
                          <TextWithTooltip
                            text={`Description: ${
                              deliverableMediaRightsData?.data?.types.find(
                                (t: any) => t.name === adRightsItem.adRightsType
                              )?.description || ""
                            }`}
                            textClassName={styles.adRightsNotes}
                            limit={100}
                          />
                        )}

                      {adRightsItem.adRightsType === "Custom" && (
                        <TextArea
                          style={{ marginTop: 4 }}
                          placeholder="Custom rights details..."
                          value={adRightsItem.adRightsNotes}
                          disabled={isItemDisabled}
                          onChange={(e) =>
                            onValueChange(
                              adRightsIndex,
                              "adRightsNotes",
                              e.target.value
                            )
                          }
                          autoSize={{ minRows: 2, maxRows: 4 }}
                        />
                      )}
                    </Flex>
                  )}

                  {/* Applies To Checkable Tags */}
                  {showAppliesToRow && (
                    <Flex vertical gap={2}>
                      <span className={styles.adRightsSubLabel}>Applies to</span>
                      {contentDeliverables.length === 0 ? (
                        <Typography.Text type="secondary" style={{ fontSize: 10 }}>
                          No content formats added yet.
                        </Typography.Text>
                      ) : (
                        <Flex wrap gap={4}>
                          {contentDeliverables
                            .filter((contentDel) => {
                              if (!isItemDisabled) return true;
                              return (
                                Boolean(linkedId) &&
                                (linkedId === contentDel.clientSideId ||
                                  (Boolean(contentDel._id) &&
                                    linkedId === contentDel._id))
                              );
                            })
                            .map((contentDel) => {
                            const isLinked =
                              Boolean(linkedId) &&
                              (linkedId === contentDel.clientSideId ||
                                (Boolean(contentDel._id) &&
                                  linkedId === contentDel._id));

                            return (
                              <Tag.CheckableTag
                                key={contentDel.clientSideId || contentDel._id}
                                checked={isLinked}
                                className={
                                  isLinked
                                    ? styles.appliesToTagChecked
                                    : styles.appliesToTag
                                }
                                style={
                                  isItemDisabled
                                    ? { pointerEvents: "none", opacity: 0.6 }
                                    : undefined
                                }
                                onChange={() =>
                                  handleToggleAppliesTo(
                                    adRightsIndex,
                                    adRightsItem,
                                    contentDel
                                  )
                                }
                                onClick={() =>
                                  handleToggleAppliesTo(
                                    adRightsIndex,
                                    adRightsItem,
                                    contentDel
                                  )
                                }
                              >
                                {getFormatLabel(contentDel)}
                              </Tag.CheckableTag>
                            );
                          })}
                        </Flex>
                      )}
                    </Flex>
                  )}

                  {/* Duration Checkable Tags */}
                  <Flex vertical gap={2}>
                    <span className={styles.adRightsSubLabel}>Duration</span>
                    <Flex wrap gap={4} align="center">
                      {PRESET_DURATIONS
                        .filter(
                          (dur) =>
                            !isItemDisabled ||
                            adRightsItem.adRightsDurations === dur
                        )
                        .map((dur) => {
                        const isChecked = adRightsItem.adRightsDurations === dur;
                        return (
                          <Tag.CheckableTag
                            key={dur}
                            checked={isChecked}
                            className={
                              isChecked
                                ? styles.appliesToTagChecked
                                : styles.appliesToTag
                            }
                            style={
                              isItemDisabled
                                ? { pointerEvents: "none", opacity: 0.6 }
                                : undefined
                            }
                            onChange={(checked) => {
                              if (!isItemDisabled) {
                                onValueChange(
                                  adRightsIndex,
                                  "adRightsDurations",
                                  checked ? dur : ""
                                );
                              }
                            }}
                            onClick={() => {
                              if (!isItemDisabled) {
                                onValueChange(
                                  adRightsIndex,
                                  "adRightsDurations",
                                  isChecked ? "" : dur
                                );
                              }
                            }}
                          >
                            {dur}
                          </Tag.CheckableTag>
                        );
                      })}

                      {/* Custom Duration Tag */}
                      {(() => {
                        const currentDur = adRightsItem.adRightsDurations || "";
                        const isCustom =
                          Boolean(currentDur) &&
                          !PRESET_DURATIONS.includes(currentDur);
                        if (isItemDisabled && !isCustom) {
                          return null;
                        }
                        return (
                          <Popover
                            content={
                              <Flex gap="small">
                                <InputNumber
                                  size="small"
                                  placeholder="e.g. 45"
                                  value={customDays}
                                  onChange={(val: number | string | null) => {
                                    if (typeof val === "number") {
                                      setCustomDays(val);
                                    } else if (typeof val === "string") {
                                      const parsed = parseFloat(val);
                                      setCustomDays(
                                        isNaN(parsed) ? null : parsed
                                      );
                                    } else {
                                      setCustomDays(null);
                                    }
                                  }}
                                  addonAfter="days"
                                />
                                <Button
                                  size="small"
                                  type="primary"
                                  onClick={() => {
                                    if (customDays && !isItemDisabled) {
                                      onValueChange(
                                        adRightsIndex,
                                        "adRightsDurations",
                                        `${customDays} days`
                                      );
                                      setActivePopoverIndex(null);
                                      setCustomDays(null);
                                    }
                                  }}
                                >
                                  Save
                                </Button>
                              </Flex>
                            }
                            title="Custom Duration"
                            trigger={isItemDisabled ? [] : "click"}
                            open={isPopoverOpen}
                            onOpenChange={(open) => {
                              if (!open) {
                                setActivePopoverIndex(null);
                                setCustomDays(null);
                              } else {
                                setActivePopoverIndex(adRightsIndex);
                              }
                            }}
                          >
                            <Tag.CheckableTag
                              checked={isCustom}
                              className={
                                isCustom
                                  ? styles.appliesToTagChecked
                                  : styles.appliesToTag
                              }
                              style={
                                isItemDisabled
                                  ? { pointerEvents: "none", opacity: 0.6 }
                                  : undefined
                              }
                              onChange={() => {
                                if (!isItemDisabled) setActivePopoverIndex(adRightsIndex);
                              }}
                            >
                              {isCustom ? `${adRightsItem.adRightsDurations}(Custom)` : "Custom"}
                            </Tag.CheckableTag>
                          </Popover>
                        );
                      })()}
                    </Flex>
                  </Flex>
                </Flex>
              </Card>
            );
          }
        ) : <span style={{ color: "#a3acb9", fontSize: 10 }}>Optional — add usage rights</span>}
      </div>
    </Flex>
  );
};
